import { Link } from "react-router-dom";
import { Scale, Gavel, ArrowRight, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { RetentionBanner, LocalVaultCard } from "@/components/LocalVault";
import { TopBar } from "@/components/PageShell";
import ImageSlot from "@/components/ImageSlot";

/**
 * Agregador `/calculos` — mostra as duas funcionalidades do módulo Atualização
 * de Cálculos:
 *  1. Atualização de Cálculos (módulo existente — preservado 100%)
 *  2. Liquidação Trabalhista (novo módulo — Fase 0 em diante)
 */
export default function CalculosHome() {
  const [retention, setRetention] = useState(null);
  useEffect(() => {
    api.get("/liq/retention/policy").then(r => setRetention(r.data)).catch(() => {});
  }, []);

  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid="calculos-home">
      <TopBar/>
      <div className="p-6 lg:p-8 w-full font-sans">
        <RetentionBanner retention={retention} />

        {/* Hero — imagem 4K institucional (slot: calculos_hero) */}
        <section className="relative rounded-2xl overflow-hidden border h-[340px] my-6" style={{ borderColor: "#E7E5E2" }} data-testid="calculos-hero">
          <ImageSlot slot="calculos_hero" alt="Atualização de Cálculos · Hermes" className="absolute inset-0 w-full h-full object-cover"/>
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(11,27,47,0.82) 0%, rgba(11,27,47,0.55) 40%, rgba(11,27,47,0.12) 78%, rgba(11,27,47,0) 100%)" }}/>
          <div className="relative h-full flex flex-col justify-center px-8 md:px-12 max-w-2xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-xl inline-flex items-center justify-center backdrop-blur" style={{ background: "rgba(240,211,122,0.18)", border: "1px solid rgba(240,211,122,0.55)" }}>
                <Scale className="w-5 h-5" style={{ color: "#F0D37A" }}/>
              </div>
              <div className="text-[11px] uppercase tracking-[0.28em] font-semibold" style={{ color: "#F0D37A" }}>
                HERMES · Cálculos
              </div>
            </div>
            <h1 className="font-serif text-5xl md:text-6xl leading-[1.02] tracking-tight text-white" style={{ textShadow: "0 2px 24px rgba(0,0,0,0.4)" }}>
              Atualização de <span className="italic" style={{ color: "#F5DE8C" }}>Cálculos</span>
            </h1>
            <p className="mt-4 font-serif italic text-lg text-white/85" style={{ textShadow: "0 1px 12px rgba(0,0,0,0.45)" }}>
              "Precisão hoje. Segurança sempre."
            </p>
          </div>
        </section>

        <p className="mt-2 text-sm text-slate-600 max-w-2xl">
          Duas ferramentas complementares para lidar com cálculos trabalhistas e
          cíveis: atualize valores de sentenças já existentes ou monte uma
          liquidação completa a partir do processo.
        </p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Link
            to="/calculos/atualizacao"
            data-testid="calculos-card-atualizacao"
            className="group relative rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#d4a24c] hover:shadow-lg transition-all"
          >
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-[#f4ead4] text-[#7c5a1e] inline-flex items-center justify-center">
                <Scale className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
                  Ferramenta consolidada
                </div>
                <h2 className="mt-1 font-heading text-xl font-semibold text-slate-900">
                  Atualização de Cálculos
                </h2>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  Atualize valores de sentenças, acórdãos ou títulos executivos
                  já formados. OCR, motor determinístico, correção pelo BCB,
                  Fazenda Pública, art. 523, matriz Sentença × Acórdão e
                  memória de cálculo em PDF com sua logomarca.
                </p>
              </div>
            </div>
            <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-[#7c5a1e] group-hover:gap-2 transition-all">
              Abrir ferramenta <ArrowRight className="w-4 h-4" />
            </div>
          </Link>

          <Link
            to="/calculos/liquidacao-trabalhista"
            data-testid="calculos-card-liquidacao"
            className="group relative rounded-2xl border border-slate-900 bg-slate-900 text-white p-6 hover:border-[#d4a24c] hover:shadow-xl transition-all overflow-hidden"
          >
            <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#d4a24c] text-slate-900 text-[10px] font-semibold uppercase tracking-wide">
              <Sparkles className="w-3 h-3" /> Beta
            </div>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-[#d4a24c]/20 text-[#d4a24c] inline-flex items-center justify-center">
                <Gavel className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#d4a24c]/80">
                  Liquidação inteligente
                </div>
                <h2 className="mt-1 font-heading text-xl font-semibold">
                  Liquidação Trabalhista
                </h2>
                <p className="mt-2 text-sm text-white/75 leading-relaxed">
                  Envie o processo inteiro. O Hermes lê, consolida o título,
                  identifica parâmetros, pergunta apenas o que falta, calcula
                  competência por competência e prepara auditoria ou
                  impugnação — nada é inventado.
                </p>
              </div>
            </div>
            <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-[#d4a24c] group-hover:gap-2 transition-all">
              Abrir módulo <ArrowRight className="w-4 h-4" />
            </div>
          </Link>
        </div>

        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="calculos-notice">
          <b>Preserva o que já funciona.</b> A ferramenta anterior está intacta —
          apenas mudou de endereço para <code className="px-1 py-0.5 bg-white/70 rounded">/calculos/atualizacao</code>.
          Todos os cálculos, PDFs e histórico continuam disponíveis.
        </div>

        <div className="mt-4">
          <LocalVaultCard />
        </div>
      </div>
    </div>
  );
}
